import { Filter } from 'common/repositories/common.model';
import { sliceDateString } from 'common/utils/date';
import regex from 'common/utils/regex';
import produce from 'immer';
import React from 'react';

export default function useFilterHandleTransformer(
  filters: Filter[],
  setFilters: React.Dispatch<React.SetStateAction<Filter[]>>,
  setPage?: React.Dispatch<React.SetStateAction<number>>,
) {
  const onClearFilters = React.useCallback(
    (filters: Filter[] = []) => {
      if (filters.length === 0) {
        setFilters((previous: Filter[] | undefined) => {
          return (previous || []).map((curr) => {
            return { ...curr, value: undefined };
          });
        });
      } else {
        setFilters((previous: Filter[] | undefined) => {
          const newFilters = (previous || []).map((curr) => {
            const matchedFilter = filters.find((f) => f.name === curr.name);
            if (matchedFilter) {
              return { ...matchedFilter, value: undefined };
            }
            return curr;
          });
          return newFilters;
        });
      }
      setPage?.(1);
    },
    [setFilters, setPage],
  );

  const onClearFilter = React.useCallback(
    (name: string) => () => {
      setFilters((prevFilters) =>
        produce(prevFilters, (draft) => {
          const matchedFilter = draft?.find((f) => f.name === name);
          if (matchedFilter) {
            matchedFilter.value = undefined;
          }
        }),
      );
      setPage?.(1);
    },
    [setFilters, setPage],
  );

  const onChangeFilters = React.useCallback(
    (filters: Filter[]) => {
      setFilters((prev) => {
        const _filters = prev.map((item) => {
          const matchedFilter = filters.find((f) => f.name === item.name);
          return matchedFilter ? matchedFilter : item;
        });
        return _filters;
      });
      setPage?.(1);
    },
    [setFilters, setPage],
  );

  const onChangeFilter = React.useCallback(
    (name: string, value) => {
      setFilters((prevFilters) =>
        produce(prevFilters, (draft) => {
          const matchedFilter = draft?.find((f) => f.name === name);

          if (matchedFilter) {
            if (typeof value === 'string' && regex.date.test(value)) {
              matchedFilter.value = sliceDateString(value);
            } else {
              matchedFilter.value = value;
            }
          }
        }),
      );
      setPage?.(1);
    },
    [setFilters, setPage],
  );

  const onSelected = React.useCallback(
    (name: string) => (value) => {
      setFilters((prevFilter) =>
        produce(prevFilter, (draft) => {
          const matchedFilter = draft?.find((f) => f.name === name);
          if (matchedFilter) {
            if (typeof value === 'string' && regex.date.test(value)) {
              matchedFilter.value = sliceDateString(value);
            } else {
              matchedFilter.value = value;
            }
          }
        }),
      );
      setPage?.(1);
    },
    [setFilters, setPage],
  );

  const onGetSelectedFilter = React.useCallback(
    (name: string) => {
      const filter = filters.find((f) => f.name === name);
      return filter;
    },
    [filters],
  );

  const onSyncFilters = React.useCallback(
    (filters: Filter[]) => {
      setFilters(filters);
    },
    [setFilters],
  );

  const filtersHasValuesCount = filters.reduce((prev, f) => {
    if (f.value) return prev + 1;
    return prev;
  }, 0);

  const onCheckFiltersHasValues = React.useCallback(
    (_filters: Filter[] = []) => {
      if (filters.length === 0) return false;
      if (_filters.length === 0) return false;

      return filters.some((filter) => {
        const curr = _filters.find((_filter) => filter.name === _filter.name);
        if (curr) {
          return !!filter.value;
        }
        return false;
      });
    },
    [filters],
  );

  return {
    filters,
    setFilters,
    onChangeFilter,
    onSelected,
    onChangeFilters,
    onClearFilter,
    onClearFilters,
    onGetSelectedFilter,
    onSyncFilters,
    filtersHasValues: filtersHasValuesCount > 0,
    filtersHasValuesCount,
    onCheckFiltersHasValues,
  };
}
